/**
 * One turn of an Explore conversation.
 *
 * An answer reads like a short paper (`AnswerBody`): paragraphs and bullets
 * with small `[n]` markers (hover = the quoted passage, click = open the
 * source at the page), a numbered **References** list after the text, and the
 * model + version that produced it — provenance, not decoration. Three answer
 * states are visibly distinct:
 *
 * - **grounded** — cited, the normal case;
 * - **refused** — the selected documents do not answer the question: a calm,
 *   neutral note, not an error. In Explore that is all ("I can't answer that
 *   from the selected documents."). In 1st line support the chat's owner is
 *   offered **Create a ticket**
 *   (escalate the question to the support team, ADR-0031); once raised the
 *   button becomes a non-clickable "Ticket DQ-000042", also after a reload
 *   (`MessageOut.ticket_number`). A read-only viewer of a shared chat gets no
 *   button — the backend would refuse them anyway;
 * - **not grounded** — an answer with no checked citation (or a legacy
 *   ungrounded-chat answer): marked so nobody mistakes it for a sourced one.
 */

import { useState } from "react";
import { Badge, Button, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconFileSearch, IconRobot, IconTicket, IconUser } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import type { MessageOut } from "@/shared/api/ai";
import { raiseTicket } from "@/shared/api/tickets";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { AnswerBody } from "./AnswerBody";
import { toCitation } from "./citations";

function Provenance({ message }: { message: MessageOut }) {
  const model = message.model_reported ?? message.model;
  if (!model) return null;
  return (
    <Text size="xs" c="dimmed" data-testid="answer-model">
      {model}
      {message.total_tokens ? ` · ${message.total_tokens} tokens` : ""}
    </Text>
  );
}

/** Where a ticket can be raised from: the chat, and whether the viewer owns it. */
export interface TicketContext {
  chatId: string;
  canRaise: boolean;
}

function TicketAction({
  message,
  context,
  number,
  onRaised,
}: {
  message: MessageOut;
  context: TicketContext;
  number: string | null;
  onRaised: (ticketNumber: string) => void;
}) {
  const getToken = useAccessToken();
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (number) {
    return (
      <Stack gap={4} mt={4}>
        <Button
          size="xs"
          variant="light"
          color="gray"
          leftSection={<IconTicket size={14} />}
          disabled
          style={{ alignSelf: "flex-start" }}
          data-testid="ticket-raised"
        >
          Ticket {number}
        </Button>
        {note && (
          <Text size="xs" c="dimmed" data-testid="ticket-note">
            {note}
          </Text>
        )}
      </Stack>
    );
  }
  if (!context.canRaise) return null;

  const onRaise = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const token = await getToken();
      const { ticket } = await raiseTicket(token, context.chatId, message.message_id);
      const notified = ticket.email_status === "sent";
      const detail = notified
        ? "the support team has been notified."
        : "the support team will see it in the tickets list.";
      onRaised(ticket.ticket_number);
      setNote(`Ticket ${ticket.ticket_number} created — ${detail}`);
      toastSuccess(
        `Ticket ${ticket.ticket_number} created`,
        `Your question was sent to support; ${detail}`,
      );
    } catch (err) {
      toastApiError("Could not create a ticket", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="xs"
      variant="light"
      leftSection={<IconTicket size={14} />}
      loading={busy}
      onClick={() => void onRaise()}
      style={{ alignSelf: "flex-start" }}
      mt={4}
      data-testid="ticket-create"
    >
      Create a ticket
    </Button>
  );
}

function RefusalBubble({ message, ticket }: { message: MessageOut; ticket: TicketContext | null }) {
  const [number, setNumber] = useState<string | null>(message.ticket_number ?? null);
  const offer = ticket?.canRaise === true && !number;
  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      maw="min(46rem, 85%)"
      bg="var(--mantine-color-default-hover)"
      data-testid="answer-refused"
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color="gray" radius="xl" size="md">
          <IconFileSearch size={16} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            Not in the selected documents
          </Text>
          <Text size="sm" c="dimmed">
            {ticket
              ? "The answer to this question was not found in the documents."
              : "I can't answer that from the selected documents."}
            {offer ? " Should I create a ticket?" : ""}
          </Text>
          {ticket && (
            <TicketAction message={message} context={ticket} number={number} onRaised={setNumber} />
          )}
          <Provenance message={message} />
        </Stack>
      </Group>
    </Paper>
  );
}

function AnswerBubble({ message }: { message: MessageOut }) {
  const citations = (message.citations ?? []).map(toCitation);
  const ungrounded = message.grounded !== true;
  return (
    <Paper withBorder radius="md" px="md" py="sm" maw="min(52rem, 90%)" data-testid="answer">
      <Stack gap={8}>
        {ungrounded && (
          <Badge
            color="orange"
            variant="light"
            size="sm"
            radius="sm"
            style={{ textTransform: "none", alignSelf: "flex-start" }}
            data-testid="answer-ungrounded"
          >
            {message.grounded === false
              ? "Not grounded — no citation could be checked"
              : "Ungrounded (legacy chat) — no sources"}
          </Badge>
        )}
        <AnswerBody content={message.content} citations={citations} />
        <Provenance message={message} />
      </Stack>
    </Paper>
  );
}

export function ExploreMessage({
  message,
  ticket = null,
}: {
  message: MessageOut;
  /** Null in Explore (or outside a saved chat) — no ticket action at all. */
  ticket?: TicketContext | null;
}) {
  const isUser = message.role === "user";
  return (
    <Group align="flex-start" gap="sm" wrap="nowrap" justify={isUser ? "flex-end" : "flex-start"}>
      {!isUser && <IconRobot size={18} style={{ marginTop: 6, flexShrink: 0 }} />}
      {isUser ? (
        <Paper
          withBorder
          radius="md"
          p="sm"
          maw="min(46rem, 85%)"
          bg="var(--mantine-primary-color-light)"
        >
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {message.content}
          </Text>
        </Paper>
      ) : message.refused ? (
        <RefusalBubble message={message} ticket={ticket} />
      ) : (
        <AnswerBubble message={message} />
      )}
      {isUser && <IconUser size={18} style={{ marginTop: 6, flexShrink: 0 }} />}
    </Group>
  );
}
